import type { ReactNode } from "react";

import { downloadUrl, isSettled, useDeleteFileOperation } from "../../api/fileOperations";
import type { FileOperation, FileOperationState } from "../../api/types";
import { Button } from "../../ui/Button";

/**
 * The shared history table for exports and imports. State is spelled out in words rather than
 * shown as a bar, because §2.3 deliberately withholds a progress percentage: an archive's size
 * is not known until it has been built, so any number would be invented.
 */

const STATE_LABELS: Record<FileOperationState, string> = {
  creating: "Preparing…",
  uploading: "Saving…",
  complete: "Ready",
  error: "Failed",
  expired: "Expired",
};

/** Byte counts arrive as a decimal string, because they are a bigint on the wire. */
export function formatSize(size: string | undefined): string {
  const bytes = Number(size ?? "0");
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "—";
  }
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 && unit > 0 ? value.toFixed(1) : String(Math.round(value))} ${units[unit] ?? "B"}`;
}

export function FileOperationList({
  label,
  operations,
  pending,
  error,
  showDownload = true,
}: {
  label: string;
  operations: FileOperation[];
  pending: boolean;
  error: Error | null;
  showDownload?: boolean;
}): ReactNode {
  const remove = useDeleteFileOperation();

  if (pending) {
    return (
      <p className="text-sm text-muted" role="status">
        Loading…
      </p>
    );
  }
  if (error !== null) {
    return (
      <p className="text-sm text-danger" role="alert">
        {error.message}
      </p>
    );
  }
  if (operations.length === 0) {
    return <p className="text-sm text-muted">Nothing here yet.</p>;
  }

  return (
    <ul aria-label={label} className="divide-y divide-line rounded-md border border-line">
      {operations.map((operation) => (
        <li key={operation.id} className="flex items-center justify-between gap-4 px-4 py-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{operation.name}</p>
            <p className="text-xs text-muted">
              <span data-testid={`state-${operation.id}`}>{STATE_LABELS[operation.state]}</span>
              {operation.state === "complete" && ` · ${formatSize(operation.size)}`}
            </p>
            {operation.error != null && operation.error !== "" && (
              <p
                className={
                  operation.state === "error"
                    ? "mt-1 text-xs text-danger"
                    : "mt-1 text-xs text-muted"
                }
                role={operation.state === "error" ? "alert" : undefined}
              >
                {operation.error}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {showDownload && operation.state === "complete" && (
              <a
                href={downloadUrl(operation)}
                className="inline-flex h-9 items-center rounded-md border border-line bg-raised px-3 text-sm font-medium hover:bg-hover"
              >
                Download
              </a>
            )}
            <Button
              size="sm"
              variant="danger"
              aria-label={`Delete ${operation.name}`}
              disabled={remove.isPending || !isSettled(operation)}
              onClick={() => {
                remove.mutate({ id: operation.id });
              }}
            >
              Delete
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
}
